import { Field, ErrorMessage, FieldArray } from 'formik';
import React, { useEffect, useState } from 'react';
import { getDealCalculation, getFinanceCostSimulator } from '../../../../../redux/actions/transactionApi';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '../DebouncedValue';
import PropertyFormat, {
    RemoveUnderscoreWithUppercase,
} from '../../../../../components/commonLayouts/PropertyFormat/PropertyFormat';
import NumberRange from '../../../../../components/formInputs/numberRange/NumberRange';
import { roundPriceInput } from '../../../../../utils/displayRounding';
import {
    buildDealCalculationPayload,
    mapDealCalculationProductsWithParsedCommission,
} from '../RequestFinancing.helpers';
import {
    getSimulatorSellingPrice,
    getSimulatorMarginValue,
    getCommissionTypeLabel,
    getProductRowKey,
    getSimulatorRowKey,
    resolveSimulatorPartyIds,
    nextProductRowKey,
} from './ProductDetails.helpers';

const ProductVolumeOrPriceField = ({ name, onBlurExtra, decimalPlaces, inputType }) => (
    <Field name={name}>
        {({ field, form }) => (
            <>
                <NumberRange
                    value={field.value}
                    onChange={(newValue) => {
                        form.setFieldValue(field.name, newValue);
                    }}
                    onBlur={(e) => {
                        field.onBlur(e);
                        if (onBlurExtra) {
                            onBlurExtra(e);
                        }
                    }}
                    allowDecimals={true}
                    decimalPlaces={decimalPlaces}
                    inputType={inputType}
                    min={0}
                />
            </>
        )}
    </Field>
);

const ProductDetailRow = ({
    product,
    index,
    formikProps,
    productTypes,
    platformRole,
    debouncedProducts,
    financeCostSimulator,
    prod_uildCheck,
    handleProductTypeChange,
    simulatorCalculation,
    remove,
    removeData,
}) => {
    const selectedOptions = formikProps.values.products.filter((p, i) => i !== index)?.map((p) => p.type);
    const commissionType = debouncedProducts[index]?.commission_type;
    return (
        <div key={getProductRowKey(product)} className='row mb-4 border-bottom pb-3'>
            <div className='col-10'>
                <div className='row m-0 text-start'>
                    <div className='col-4 mb-3'>
                        <label className='form-label text-neutral-800 font-b3 mb-1'>Product Name</label>
                        <Field
                            as='select'
                            name={`products[${index}].type`}
                            className='form-control font-b2'
                            onChange={(e) => handleProductTypeChange(index, e.target.value)}>
                            <option value='' label='Select Option' />
                            {productTypes?.map((typeOption) => (
                                <option
                                    key={typeOption.ulid || typeOption.product_ulid}
                                    value={typeOption.ulid || typeOption.product_ulid}
                                    disabled={selectedOptions.includes(typeOption.ulid || typeOption.product_ulid)}>
                                    {typeOption.name}
                                </option>
                            ))}
                        </Field>
                    </div>

                    <div className='col-3 mb-3'>
                        <label className='form-label text-neutral-800 font-b3 mb-1'>Min Volume (MT) (Optional)</label>
                        <ProductVolumeOrPriceField
                            name={`products[${index}].min_volume`}
                            decimalPlaces={4}
                            inputType='volume'
                        />
                        <ErrorMessage
                            name={`products[${index}].min_volume`}
                            component='div'
                            className='error-message font-b4 text-error-700'
                        />
                    </div>

                    <div className='col-2 mb-3'>
                        <label className='form-label text-neutral-800 font-b3 mb-1'>Volume (MT)</label>
                        <ProductVolumeOrPriceField
                            name={`products[${index}].volume`}
                            decimalPlaces={4}
                            inputType='volume'
                            onBlurExtra={(e) => simulatorCalculation(index, e)}
                        />
                        <ErrorMessage
                            name={`products[${index}].volume`}
                            component='div'
                            className='error-message font-b4 text-error-700'
                        />
                    </div>

                    <div className='col-2 mb-3'>
                        <label className='form-label text-neutral-800 font-b3 mb-1'>Price</label>
                        <ProductVolumeOrPriceField
                            name={`products[${index}].price`}
                            decimalPlaces={3}
                            inputType='price'
                            onBlurExtra={(e) => simulatorCalculation(index, e)}
                        />
                        <ErrorMessage
                            name={`products[${index}].price`}
                            component='div'
                            className='error-message font-b4 text-error-700'
                        />
                    </div>

                    {platformRole === 'broker' && (
                        <>
                            <div className='col-2 mb-3'>
                                <label className='form-label text-neutral-800 font-b3 mb-1'>Selling Price</label>
                                <Field name={`products[${index}].margin`}>
                                    {({ field, form }) => (
                                        <>
                                            <NumberRange
                                                value={field.value}
                                                onChange={(newValue) => {
                                                    form.setFieldValue(field.name, newValue);
                                                }}
                                                onBlur={(e) => {
                                                    field.onBlur(e);
                                                    simulatorCalculation(index, e);
                                                }}
                                                allowDecimals={true}
                                                decimalPlaces={3}
                                                inputType='price'
                                                min={0}
                                                disabled={commissionType === 'lumpsum'}
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage
                                    name={`products[${index}].margin`}
                                    component='div'
                                    className='error-message font-b4 text-error-700'
                                />
                            </div>

                            <div className='col-3 mb-3'>
                                <label className='form-label text-neutral-800 font-b3 mb-1'>
                                    Margin ({getCommissionTypeLabel(commissionType)})
                                </label>
                                <Field name={`products[${index}].commission`}>
                                    {({ field, form }) => (
                                        <>
                                            <NumberRange
                                                value={field.value}
                                                onChange={(newValue) => {
                                                    form.setFieldValue(field.name, newValue);
                                                }}
                                                onBlur={(e) => {
                                                    field.onBlur(e);
                                                    simulatorCalculation(index, e);
                                                }}
                                                allowDecimals={true}
                                                decimalPlaces={4}
                                                inputType='price'
                                                min={0}
                                                disabled={commissionType === 'lumpsum'}
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage
                                    name={`products[${index}].commission`}
                                    component='div'
                                    className='error-message font-b4 text-error-700'
                                />
                            </div>
                            <div className='col-4 mt-4'>
                                <table className='table table-bordered'>
                                    <thead className='table-light'>
                                        <tr>
                                            <th>Days</th>
                                            <th>Financing Cost (Per MT)</th>
                                            <th>Total Cost (Per MT)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productTypes?.map((typeOption) => {
                                            if (typeOption.product_ulid === prod_uildCheck[index]) {
                                                const rows = financeCostSimulator[index] || [];
                                                return rows.map((row) => (
                                                    <tr key={getSimulatorRowKey(row)}>
                                                        <td style={{ width: '30%' }}>
                                                            {PropertyFormat(row.days, RemoveUnderscoreWithUppercase)}
                                                        </td>
                                                        <td style={{ width: '40%' }}>${row.financing_cost}</td>
                                                        <td style={{ width: '30%' }}>${row.final_price}</td>
                                                    </tr>
                                                ));
                                            }
                                            return null;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {formikProps.values.products.length > 1 && (
                <div className='col-2 d-flex align-items-start justify-content-end'>
                    <span
                        onClick={() => {
                            remove(index);
                            removeData(index);
                        }}
                        className='sf-delete font-h1 cursor-pointer text-danger mt-2'
                        title='Remove Product'
                    />
                </div>
            )}
        </div>
    );
};

const ProductDetails = (props) => {
    const dispatch = useDispatch();
    const [tempIndexVal, setTempIndexVal] = useState(0);
    const [financeCostSimulator, setFinanceCostSimulator] = useState([]);
    const debouncedProducts = useDebounce(props.formikProps.values.products, 500);
    const [prod_uildCheck, setprod_uildCheck] = useState([]);
    const debouncedCharges = useDebounce(props.formikProps.values.products, 500);
    const state = useSelector((state) => {
        return state.app;
    });
    useEffect(() => {
        const payload = buildDealCalculationPayload({
            platformRole: props.platformRole,
            orgId: props.orgId,
            associatedBuyer: props.associatedBuyer,
            buyerSupplierName: props.formikProps.values.buyer_supplier_name,
            products: debouncedProducts,
            charges: debouncedCharges,
            paymentTerm: props.formikProps.values.payment_term,
            earlyPayment: props.formikProps.values.early_payment_options,
            extendedCredit: props.formikProps.values.extended_credit_option,
            checked: props.formikProps.values.checked,
            transactionType: 'deal_financing',
            productMapper: mapDealCalculationProductsWithParsedCommission,
            chargeProductNameKey: 'name',
        });
        payload.role = props?.platformRole;
        dispatch(getDealCalculation(payload));
    }, [debouncedProducts, debouncedCharges, props.platformRole]);
    const simulatorCalculation = (index, e) => {
        setTempIndexVal(index);
        const { buyerId, brokId } = resolveSimulatorPartyIds({
            platformRole: props.platformRole,
            orgId: props.orgId,
            associatedBuyer: props.associatedBuyer,
        });

        const payload = {
            buyer_organization_ulid: buyerId || '',
            broker_organization_ulid: brokId || '',
            product: {
                product_ulid: debouncedProducts[index].type,
                price: Number(debouncedProducts[index].price),
                volume: Number(debouncedProducts[index].volume),
                selling_price: getSimulatorSellingPrice(debouncedProducts[index], e.target.name, index),
                product_name: debouncedProducts[index].name || '',
                margin_value: parseFloat(getSimulatorMarginValue(debouncedProducts[index], e.target.name, index)),
                margin_type: debouncedProducts[index].commission_type || '',
            },
        };

        if (debouncedProducts[index].volume !== '' && debouncedProducts[index].price !== '') {
            dispatch(getFinanceCostSimulator(payload));
        }
    };
    useEffect(() => {
        const simulatorData = state.transaction?.financeCostSimulator?.simulator_result;
        const product = state.transaction?.financeCostSimulator?.product;
        const simulatorDataUlid = product?.product_ulid;

        if (simulatorData && Array.isArray(simulatorData) && simulatorDataUlid) {
            const commissionValue = product.margin_value ? roundPriceInput(product.margin_value) : '0.00';
            props.formikProps.setFieldValue(`products[${tempIndexVal}].commission`, commissionValue);
            const sellingPrice = product.selling_price ? roundPriceInput(product.selling_price) : '0.00';
            props.formikProps.setFieldValue(`products[${tempIndexVal}].margin`, sellingPrice);

            setprod_uildCheck((prevUlids) => {
                const existingIndex = prevUlids.indexOf(simulatorDataUlid);
                if (existingIndex === -1) {
                    setFinanceCostSimulator((prevSim) => [...prevSim, simulatorData]);
                    return [...prevUlids, simulatorDataUlid];
                }
                setFinanceCostSimulator((prevSim) => {
                    const updated = [...prevSim];
                    updated[existingIndex] = simulatorData;
                    return updated;
                });
                return prevUlids;
            });
        }
    }, [state.transaction?.financeCostSimulator]);

    const handleProductTypeChange = (index, value) => {
        const selectedProduct = props.productTypes.find((p) =>
            props.platformRole !== 'broker' ? p.ulid === value : p.product_ulid === value
        );
        const name = selectedProduct?.name || '';
        props.formikProps.setFieldValue(`products[${index}].type`, value);
        props.formikProps.setFieldValue(`products[${index}].name`, name);

        const productType = props.productTypes?.find((type) => type.product_ulid === value);
        if (productType) {
            const { brokerage_commission_type, brokerage_commission_value } = productType;
            const commissionValue = brokerage_commission_value ? roundPriceInput(brokerage_commission_value) : '';
            props.formikProps.setFieldValue(`products[${index}].commission`, commissionValue);
            props.formikProps.setFieldValue(`products[${index}].commission_type`, brokerage_commission_type || '');
        }
    };

    const removeData = (ind) => {
        const newData = financeCostSimulator.filter((_, index) => index !== ind);
        setFinanceCostSimulator(newData);
    };
    return (
        <div className='accordion-item'>
            <h2 className='accordion-header' id='flush-headingTwo'>
                <button
                    className='accordion-button collapsed accordion-title px-3'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target='#flush-collapseTwo'
                    aria-expanded='false'
                    aria-controls='flush-collapseTwo'>
                    Product Details{' '}
                </button>
            </h2>
            <div
                id='flush-collapseTwo'
                className='accordion-collapse collapse show'
                aria-labelledby='flush-headingTwo'
                data-bs-parent='#accordionFlushExample'>
                <div className='accordion-body p-0 px-1 pb-4'>
                    <div className='col-12'>
                        <FieldArray name='products'>
                            {({ push, remove }) => (
                                <>
                                    <div className='row'>
                                        {props.formikProps.values?.products?.map((product, index) => (
                                            <ProductDetailRow
                                                key={getProductRowKey(product)}
                                                product={product}
                                                index={index}
                                                formikProps={props.formikProps}
                                                productTypes={props.productTypes}
                                                platformRole={props.platformRole}
                                                debouncedProducts={debouncedProducts}
                                                financeCostSimulator={financeCostSimulator}
                                                prod_uildCheck={prod_uildCheck}
                                                handleProductTypeChange={handleProductTypeChange}
                                                simulatorCalculation={simulatorCalculation}
                                                remove={remove}
                                                removeData={removeData}
                                            />
                                        ))}
                                        <div className='col-12 ms-3'>
                                            {props.platformRole === 'broker' ? (
                                                <div
                                                    className='p-2 mb-3 text-start'
                                                    style={{
                                                        backgroundColor: '#f0f0f0',
                                                        color: '#000',
                                                        fontSize: '0.875rem',
                                                        borderRadius: '4px',
                                                        fontWeight: 'bold',
                                                    }}>
                                                    * Additional fees may apply, refer deal summary for details
                                                </div>
                                            ) : null}
                                            <div className='col-12 text-start'>
                                                <button
                                                    className='hollow-secondary font-medium secondary-auto'
                                                    type='button'
                                                    onClick={() =>
                                                        push({
                                                            rowKey: nextProductRowKey(),
                                                            type: '',
                                                            name: '',
                                                            volume: '',
                                                            price: '',
                                                            commission: 0,
                                                            margin: 0,
                                                        })
                                                    }>
                                                    Add Product +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </FieldArray>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetails;
